import React, { useState } from "react";
import {
  Dialog,
  DialogFooter,
  DialogType,
  PrimaryButton,
  DefaultButton,
  Stack,
  Text,
  Spinner,
  SpinnerSize,
  MessageBar,
  MessageBarType,
  Icon,
} from "@fluentui/react";
import styles from "./ExportModal.module.css";
import { ExportFormat } from "../../api/models";
import { exportConversation } from "../../api/api";

/**
 * Validates that a download URL originates from a trusted domain.
 * Only Azure Blob Storage SAS URLs and same-origin URLs are allowed.
 */
const isTrustedDownloadUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url, window.location.origin);

    // Allow same-origin (relative URLs resolved against current host)
    if (parsed.origin === window.location.origin) {
      return true;
    }

    // Allow Azure Blob Storage URLs
    if (
      parsed.protocol === "https:" &&
      parsed.hostname.endsWith(".blob.core.windows.net")
    ) {
      return true;
    }

    return false;
  } catch {
    return false;
  }
};

interface ExportModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  conversationId: string;
  /** When set, exports only this specific response instead of the whole conversation. */
  messageId?: string;
}

const FORMAT_OPTIONS: { key: ExportFormat; text: string; iconName: string; disabled?: boolean }[] = [
  { key: "pdf", text: "PDF", iconName: "PDF" },
  { key: "docx", text: "Word", iconName: "WordDocument" },
  { key: "txt", text: "Text", iconName: "TextDocument" },
];

export const ExportModal: React.FC<ExportModalProps> = ({
  isOpen,
  onDismiss,
  conversationId,
  messageId,
}) => {
  const [selectedFormat, setSelectedFormat] = useState<ExportFormat>("docx");
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleExport = async () => {
    setIsExporting(true);
    setError(null);

    try {
      const result = await exportConversation({
        conversation_id: conversationId,
        format: selectedFormat,
        ...(messageId ? { message_id: messageId } : {}),
      });

      if (result.error) {
        setError(result.error);
      } else if (result.download_url) {
        if (!isTrustedDownloadUrl(result.download_url)) {
          setError("Download blocked: the file URL is not from a trusted source.");
          return;
        }
        // Navigate directly to the download URL. The backend serves the
        // file with Content-Disposition: attachment, so the browser
        // downloads without navigating away from the SPA.
        // This avoids blob: URL issues where Chrome blocks programmatic
        // downloads when the original user gesture is lost in async chains.
        window.location.href = result.download_url;
        // Auto-close modal after triggering download
        handleDismiss();
      }
    } catch {
      setError("Export service is unavailable. Please try again later.");
    } finally {
      setIsExporting(false);
    }
  };

  const handleDismiss = () => {
    setError(null);
    setIsExporting(false);
    onDismiss();
  };

  const dialogContentProps = {
    type: DialogType.largeHeader,
    title: messageId ? "Export Response" : "Export Conversation",
    subText: messageId
      ? "Choose a format to export this individual response including its content and citations."
      : "Choose a format to export the entire conversation including all messages, formatting, and citations.",
  };

  const modalProps = {
    isBlocking: isExporting,
    overlay: {
      styles: {
        root: {
          backgroundColor: "rgba(0, 0, 0, 0.4)",
        },
      },
    },
    styles: {
      main: {
        selectors: {
          ["@media (min-width: 480px)"]: {
            minWidth: 400,
            maxWidth: 440,
          },
        },
      },
    },
  };

  return (
    <Dialog
      hidden={!isOpen}
      onDismiss={handleDismiss}
      dialogContentProps={dialogContentProps}
      modalProps={modalProps}
    >
      <Stack tokens={{ childrenGap: 16 }}>
        {/* Format Selection */}
        {!error && (
          <div className={styles.formatTiles} role="radiogroup" aria-label="Export format">
            {FORMAT_OPTIONS.map(opt => {
              const isSelected = selectedFormat === opt.key;
              const isDisabled = opt.disabled || isExporting;
              return (
                <button
                  key={opt.key}
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  disabled={isDisabled}
                  className={[
                    styles.formatTile,
                    isSelected ? styles.formatTileSelected : "",
                    isDisabled ? styles.formatTileDisabled : "",
                  ].filter(Boolean).join(" ")}
                  onClick={() => !isDisabled && setSelectedFormat(opt.key)}
                >
                  <span className={styles.formatTileRadio}>
                    {isSelected && <span className={styles.formatTileRadioDot} />}
                  </span>
                  <Icon
                    iconName={opt.iconName}
                    styles={{ root: { fontSize: 32, width: 32, height: 32, lineHeight: "32px" } }}
                  />
                  <span className={styles.formatTileLabel}>{opt.text}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Loading State */}
        {isExporting && (
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 12 }} className={styles.exportingState}>
            <Spinner size={SpinnerSize.medium} />
            <Text variant="medium">Generating your {selectedFormat.toUpperCase()} file...</Text>
          </Stack>
        )}

        {/* Error State */}
        {error && (
          <Stack tokens={{ childrenGap: 8 }}>
            <MessageBar messageBarType={MessageBarType.error} className={styles.resultMessage}>
              {error}
            </MessageBar>
            <Text variant="small" style={{ color: "#666" }}>
              You can retry or use the copy button on individual messages as a fallback.
            </Text>
          </Stack>
        )}
      </Stack>

      <DialogFooter>
        {!error && (
          <div className={styles.footerButtons}>
            <PrimaryButton
              onClick={handleExport}
              disabled={isExporting}
              text={isExporting ? "Exporting..." : "Export"}
              iconProps={isExporting ? undefined : { iconName: "Download" }}
            />
            <DefaultButton onClick={handleDismiss} text="Cancel" disabled={isExporting} />
          </div>
        )}
        {error && (
          <div className={styles.footerButtons}>
            <PrimaryButton onClick={handleExport} text="Retry" iconProps={{ iconName: "Refresh" }} />
            <DefaultButton onClick={handleDismiss} text="Close" />
          </div>
        )}
      </DialogFooter>
    </Dialog>
  );
};
